// =============================================================================
// features/sales/components/ScheduleAppointmentFields.tsx — Campos do
// formulário de agendamento (data, hora, duração, responsável, canal,
// motivo). Separado de ScheduleAppointmentDialog.tsx só para manter o
// orquestrador abaixo de 200 linhas (norma anti-padrão do DS).
// =============================================================================

import type { ContactChannel } from '@elemento/shared-schemas';
import { CONTACT_CHANNELS } from '@elemento/shared-schemas';
import * as React from 'react';
import type { UseFormRegister, FieldErrors } from 'react-hook-form';

import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { CHANNEL_LABELS } from '../agenda/labels';

import { FormTextarea } from './FormTextarea';
import type { ScheduleFormValues } from './ScheduleAppointmentDialog';

const CHANNEL_OPTIONS = CONTACT_CHANNELS.map((c: ContactChannel) => ({
  value: c,
  label: CHANNEL_LABELS[c],
}));

const DURATION_OPTIONS = [15, 30, 45, 60, 90, 120].map((minutes) => ({
  value: String(minutes),
  label: `${minutes} min`,
}));

export interface AgentOption {
  value: string;
  label: string;
}

export interface ScheduleAppointmentFieldsProps {
  register: UseFormRegister<ScheduleFormValues>;
  errors: FieldErrors<ScheduleFormValues>;
  agentOptions: AgentOption[];
  disabled: boolean;
  idPrefix: string;
}

export function ScheduleAppointmentFields({
  register,
  errors,
  agentOptions,
  disabled,
  idPrefix,
}: ScheduleAppointmentFieldsProps): React.JSX.Element {
  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        <Input
          id={`${idPrefix}-date`}
          label="Data"
          type="date"
          required
          disabled={disabled}
          error={errors.date?.message}
          {...register('date')}
        />
        <Input
          id={`${idPrefix}-time`}
          label="Hora"
          type="time"
          required
          disabled={disabled}
          error={errors.time?.message}
          {...register('time')}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Select
          id={`${idPrefix}-duration`}
          label="Duração"
          options={DURATION_OPTIONS}
          disabled={disabled}
          error={errors.durationMinutes?.message}
          {...register('durationMinutes')}
        />
        <Select
          id={`${idPrefix}-channel`}
          label="Canal"
          required
          options={CHANNEL_OPTIONS}
          disabled={disabled}
          error={errors.channel?.message}
          {...register('channel')}
        />
      </div>

      <Select
        id={`${idPrefix}-agent`}
        label="Responsável"
        placeholder="Eu mesmo"
        options={agentOptions}
        disabled={disabled}
        hint="Deixe em branco para atribuir a si mesmo."
        error={errors.assignedAgentId?.message}
        {...register('assignedAgentId')}
      />

      <Input
        id={`${idPrefix}-reason`}
        label="Motivo"
        placeholder="Ex: retorno sobre proposta enviada"
        disabled={disabled}
        error={errors.reason?.message}
        {...register('reason')}
      />

      <FormTextarea
        id={`${idPrefix}-notes`}
        label="Observação"
        placeholder="Contexto adicional (opcional)"
        disabled={disabled}
        error={errors.notes?.message}
        {...register('notes')}
      />
    </>
  );
}
